import type { Routes } from '@angular/router';
import { marker } from '@jsverse/transloco-keys-manager/marker';
import { UNSUBSCRIBE_NOTIFICATIONS_TOKEN_PARAMETER } from './consts/unsubscribe.consts';
import { checkAuthenticatedGuard } from './guards/check-authenticated.guard';
import { ensureLoggedOutGuard } from './guards/ensure-logged-out.guard';

export const ROUTES: Routes = [
  {
    path: 'login',
    loadComponent: async () =>
      import('./containers/login/login.container').then(
        (module) => module.LoginContainerComponent,
      ),
    canActivate: [checkAuthenticatedGuard],
  },
  {
    path: 'login-external',
    loadComponent: async () =>
      import('./containers/external-login/external-login.component').then(
        (module) => module.ExternalLoginComponent,
      ),
  },
  {
    path: 'login-sso',
    loadComponent: async () =>
      import('./containers/login-with-sso/login-with-sso.container').then(
        (module) => module.LoginWithSSOContainerComponent,
      ),
    canActivate: [checkAuthenticatedGuard],
  },
  {
    path: 'sso/:accountName',
    loadComponent: async () =>
      import('./containers/login-with-sso-by-link/login-with-sso-by-link.container').then(
        (module) => module.LoginWithSsoByLinkContainerComponent,
      ),
    canActivate: [checkAuthenticatedGuard],
  },
  {
    path: 'logout-external',
    loadComponent: async () =>
      import('./containers/external-logout/external-logout.container').then(
        (module) => module.ExternalLogoutContainerComponent,
      ),
  },
  {
    path: 'verify/:hash',
    loadComponent: async () =>
      import('./containers/verify-login/verify-login.container').then(
        (module) => module.VerifyLoginContainerComponent,
      ),
    children: [
      {
        path: 'otp',
        loadComponent: async () =>
          import('./containers/otp-verification/otp-verification.container').then(
            (module) => module.OtpVerificationContainerComponent,
          ),
      },
    ],
  },
  {
    path: 'verifyActions',
    redirectTo: 'verify-actions',
  },
  {
    path: 'verify-actions/:hash',
    loadComponent: async () =>
      import('./containers/verify-action-login-container/verify-action-login.container').then(
        (module) => module.VerifyActionLoginContainerComponent,
      ),
    children: [
      {
        path: 'otp',
        loadComponent: async () =>
          import('./containers/otp-verification/otp-verification.container').then(
            (module) => module.OtpVerificationContainerComponent,
          ),
      },
    ],
  },
  {
    path: 'forgot-password',
    loadComponent: async () =>
      import('./containers/forgot-password/forgot-password.container').then(
        (module) => module.ForgotPasswordContainerComponent,
      ),
  },
  {
    path: 'reset-password',
    loadComponent: async () =>
      import('./containers/reset-password/reset-password.container').then(
        (module) => module.ResetPasswordContainerComponent,
      ),
    canActivate: [ensureLoggedOutGuard],
  },
  {
    path: 'registerSupplier',
    redirectTo: 'register-supplier',
  },
  {
    path: 'register-supplier',
    loadComponent: async () =>
      import('./containers/register-supplier/register-supplier.container').then(
        (module) => module.RegisterSupplierContainerComponent,
      ),
  },
  {
    /**
     * NOTE: deliberately unguarded, like `logout-external` above. This route is
     * the only thing that dispatches the session teardown, so guarding it makes
     * a broken session unrecoverable: `authGuard` would reject it, redirect to
     * `/login`, and `checkAuthenticatedGuard` would bounce back on the token
     * that only this route can clear.
     */
    path: 'logout',
    loadComponent: async () =>
      import('./containers/logout/logout.container').then(
        (module) => module.LogoutContainerComponent,
      ),
  },
  {
    path: `unsubscribe/notifications/:${UNSUBSCRIBE_NOTIFICATIONS_TOKEN_PARAMETER}`,
    loadComponent: async () =>
      import('./containers/unsubscribe-notifications/unsubscribe-notifications.container').then(
        (module) => module.UnsubscribeNotificationsContainerComponent,
      ),
  },
  {
    /**
     * NOTE: target of the LearnUpon "Unauthorized URL" setting. LearnUpon
     * redirects here when it rejects a My Academy sign-in and passes no reason.
     * Public, because suppliers and auditors have no landing page in the app.
     */
    path: 'my-academy/sign-in-failed',
    loadComponent: async () =>
      import('./containers/public-message-page/public-message-page.container').then(
        (module) => module.PublicMessagePageContainerComponent,
      ),
    data: {
      headingTranslationKey: marker('myAcademy.signInFailedPage.heading'),
      messageTranslationKey: marker('myAcademy.signInFailedPage.message'),
    },
  },
  {
    path: 'invalid-link',
    loadComponent: async () =>
      import('./containers/public-message-page/public-message-page.container').then(
        (module) => module.PublicMessagePageContainerComponent,
      ),
    data: {
      headingTranslationKey: marker('authModule.invalidLink'),
      messageTranslationKey: marker('authModule.linkNoMoreValid'),
    },
  },
  {
    path: '**',
    redirectTo: '/404',
  },
];
